
import React from 'react';
import { Button, ButtonProps, IconButton, CircularProgress, useTheme } from '@mui/material';
import { Delete } from '@mui/icons-material';

export const DeleteIcon: React.FC<ButtonProps> = ({ ...props }) => (
  <IconButton
    sx={{
      transition: 'all 0.2s ease',
      '&:hover': {
        color: 'error.main',
        transform: 'scale(1.1)',
      }
    }}
    {...props}
  >
    <Delete />
  </IconButton>
);


export const ConfirmButton: React.FC<ButtonProps> = ({ children, ...props }) => (
  <Button
    variant="contained"
    /*
    sx={{
      px: { xs: 2.5, sm: 3 },
      py: { xs: 1, sm: 1.25 },
      borderRadius: 2,
      fontWeight: 600,
      textTransform: 'none',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        transform: 'translateY(-1px)',
      },
    }}
    */
    {...props}
  >
    {children}
  </Button>
);

export const PrimaryButton: React.FC<ButtonProps & { loading?: boolean }> = ({ children, loading, sx, ...props }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  return (
    <Button
      variant="contained"
      disabled={loading || props.disabled}
      sx={{
        px: 3,
        py: 1.5,
        borderRadius: 2,
        backgroundColor: isDarkMode ? 'text.purple' : 'text.dark',
        color: 'text.white',
        fontWeight: 600,
        textTransform: 'none',
        fontSize: {
          xs: '0.72rem',
          sm: '0.765rem',
          md: '0.765rem',
          lg: '0.81rem',
          xl: '0.855rem',
        },
        '&:hover': {
          backgroundColor: isDarkMode ? 'background.purpleHover' : 'background.black',
        },
        '&:active': {
          transform: 'translateY(0)',
        },
        '&:disabled': {
          backgroundColor: 'background.darkGrey',
        },
        ...sx
      }}
      {...props}
    >
      {loading ? (
        <CircularProgress size={20} color="inherit" />
      ) : (
        children
      )}
    </Button>
  );
};

export const SecondaryButton: React.FC<ButtonProps> = ({ children, sx, ...props }) => (
  <Button
    variant="outlined"
    sx={{
      px: 3,
      py: 1.5,
      borderRadius: 2,
      borderColor: 'border.medium',
      color: 'text.darkGrey',
      backgroundColor: 'background.white',
      fontWeight: 600,
      fontSize: {
        xs: '0.72rem',
        sm: '0.765rem',
        md: '0.765rem',
        lg: '0.81rem',
        xl: '0.855rem',
      },
      '&:hover': {
        borderColor: 'border.dark',
        backgroundColor: 'background.grey',
        transform: 'translateY(-1px)',
        // inset shadow simulates a 2px border without changing box size (and layering the drop shadow)
        boxShadow: (theme: any) => `inset 0 0 0 1px ${theme.palette.border.dark}, 0 4px 6px -1px rgba(102, 126, 234, 0.1)`,
      },
      '&:active': {
        transform: 'translateY(0)',
      },
      ...sx
    }}
    {...props}
  >
    {children}
  </Button>
);

export const TertiaryButton: React.FC<ButtonProps> = ({ children, sx, ...props }) => (
  <Button
    variant="outlined"
    sx={{
      px: 3,
      py: 1.5,
      borderRadius: 2,
      borderColor: 'border.medium',
      color: 'text.white',
      backgroundColor: 'background.red',
      fontWeight: 600,
      fontSize: {
        xs: '0.72rem',
        sm: '0.765rem',
        md: '0.765rem',
        lg: '0.81rem',
        xl: '0.855rem',
      },
      '&:hover': {
        borderWidth: 2,
        borderColor: 'border.dark',
        boxShadow: '0 4px 6px -1px rgba(102, 126, 234, 0.1)',
      },
      '&:active': {
        transform: 'translateY(0)',
      },
      ...sx
    }}
    {...props}
  >
    {children}
  </Button>
);

export const QuaternaryButton: React.FC<ButtonProps> = ({ children, sx, ...props }) => (
  <Button
    variant="outlined"
    sx={{
      px: 3,
      py: 1.5,
      borderRadius: 2,
      borderColor: 'border.medium',
      color: 'text.white',
      backgroundColor: 'background.green',
      fontWeight: 600,
      fontSize: {
        xs: '0.72rem',
        sm: '0.765rem',
        md: '0.765rem',
        lg: '0.81rem',
        xl: '0.855rem',
      },
      '&:hover': {
        borderWidth: 2,
        borderColor: 'border.dark',
        boxShadow: '0 4px 6px -1px rgba(102, 126, 234, 0.1)',
      },
      '&:active': {
        transform: 'translateY(0)',
      },
      ...sx
    }}
    {...props}
  >
    {children}
  </Button>
);

export const CancelButton: React.FC<ButtonProps> = ({ children, sx, ...props }) => (
  <Button
    variant="outlined"
    sx={{
      backgroundColor: 'background.darkGrey',
      borderRadius: { xs: 1.5, sm: 2 },
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      textTransform: 'uppercase',
      fontWeight: 700,
      fontSize: {
        xs: '0.72rem',
        sm: '0.765rem',
        md: '0.765rem',
        lg: '0.81rem',
        xl: '0.855rem',
      },
      letterSpacing: '0.8px',
      fontFamily: 'inherit',
      py: { xs: 1, sm: 1.25 },
      px: { xs: 2, sm: 2.5 },
      borderColor: 'border.medium',
      color: 'text.darkGrey',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        borderColor: 'border.dark',
        backgroundColor: 'background.darkGrey',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        transform: { xs: 'none', sm: 'translateY(-1px)' },
      },
      ...sx
    }}
    {...props}
  >
    {children}
  </Button>
);

export const SubmitButton: React.FC<ButtonProps & { loading?: boolean }> = ({
  children,
  loading,
  sx,
  ...props
}) => (
  <Button
    variant="contained"
    disabled={loading || props.disabled}
    sx={{
      backgroundColor: 'background.shadowBlack',
      borderRadius: { xs: 1.5, sm: 2 },
      boxShadow: '0 4px 12px rgba(26, 26, 26, 0.3)',
      textTransform: 'uppercase',
      fontWeight: 700,
      fontSize: {
        xs: '0.72rem',
        sm: '0.765rem',
        md: '0.765rem',
        lg: '0.81rem',
        xl: '0.855rem',
      },
      letterSpacing: '0.8px',
      fontFamily: 'inherit',
      py: { xs: 1, sm: 1.25 },
      px: { xs: 2, sm: 2.5 },
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        boxShadow: '0 6px 20px rgba(26, 26, 26, 0.4)',
        transform: { xs: 'none', sm: 'translateY(-1px)' },
      },
      '&:disabled': {
        backgroundColor: 'background.darkGrey',
        color: 'text.grey',
        boxShadow: 'none',
        transform: 'none',
      },
      ...sx
    }}
    {...props}
  >
    {loading ? (
      <CircularProgress size={20} color="inherit" />
    ) : (
      children
    )}
  </Button>
);
export const DeleteButton: React.FC<ButtonProps> = ({ children, sx, ...props }) => (
  <Button
    variant="outlined"
    startIcon={<Delete />}
    sx={{
      px: 2,
      py: 1,
      borderRadius: 2,
      borderColor: 'text.red',
      color: 'text.red',
      fontWeight: 600,
      textTransform: 'none',
      fontSize: {
        xs: '0.72rem',
        sm: '0.765rem',
        md: '0.765rem',
        lg: '0.81rem',
      },
      '&:hover': {
        borderColor: 'border.redDark',
        backgroundColor: 'background.red',
        color: 'text.white'
      },
      ...sx
    }}
    {...props}
  >
    {children}
  </Button>
);

